import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Shell } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { InvoiceDialog } from "@/components/InvoiceDialog";
import { money, fmtTime, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { LayoutGrid, Boxes, ReceiptText, Plus, AlertTriangle, Loader2, RefreshCw, ChevronRight, X, Power, Search, IndianRupee, Receipt, CalendarClock, UtensilsCrossed, Eye, EyeOff } from "lucide-react";
import { ExpensesPanel } from "@/components/ExpensesPanel";
import { ClubOpsPanel } from "@/components/ClubOpsPanel";
import { StockAdjustDialog } from "@/components/StockAdjustDialog";
import { toast } from "sonner";

const NEXT = { placed: { label: "Accept", ep: "accept" }, accepted: { label: "Start Preparing", ep: "advance" },
  preparing: { label: "Mark Ready", ep: "advance" }, ready: { label: "Out for Delivery", ep: "advance" },
  out_for_delivery: { label: "Complete & Bill", ep: "advance" } };
const COLS = [
  { key: "placed", title: "New" }, { key: "accepted", title: "Accepted" },
  { key: "preparing", title: "Preparing" }, { key: "ready", title: "Ready" },
  { key: "out_for_delivery", title: "Out for Delivery" },
];

export default function StaffDashboard() {
  const { settings, reload } = useSettings();
  const sym = settings.currency_symbol || "\u20b9";
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [menu, setMenu] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewInv, setViewInv] = useState(null);
  const [adjItem, setAdjItem] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [payRemarks, setPayRemarks] = useState("");

  const loadOrders = async () => { try { const { data } = await api.get("/orders"); setOrders(data); } catch {} };
  const loadInv = async () => { try { const { data } = await api.get("/inventory"); setInventory(data); } catch {} };
  const loadMenu = async () => { try { const { data } = await api.get("/menu"); setMenu(data); } catch {} };
  const loadInvoices = async () => { try { const { data } = await api.get("/invoices"); setInvoices(data); } catch {} };

  useEffect(() => { Promise.all([loadOrders(), loadInv(), loadMenu(), loadInvoices()]).finally(() => setLoading(false));
    const t = setInterval(loadOrders, 12000); return () => clearInterval(t); }, []);

  const toggleHide = async (m) => {
    try { await api.post(`/menu/${m.id}/visibility`, { hidden: !m.hidden }); toast.success(m.hidden ? "Item unhidden" : "Item hidden"); loadMenu(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleClub = async () => {
    try {
      const { data } = await api.post(settings.club_open ? "/club/close" : "/club/open"); reload();
      toast.success(data.club_open ? "Club opened for service" : "Club closed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const act = async (o) => {
    const step = NEXT[o.status]; if (!step) return;
    try {
      const { data } = await api.post(`/orders/${o.id}/${step.ep}`);
      if (data.invoice) toast.success(`Completed — invoice ${data.invoice.invoice_number} generated`);
      else toast.success(`${o.order_number} → ${step.label}`);
      await loadOrders(); loadInvoices(); loadInv();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (o) => {
    try { await api.post(`/orders/${o.id}/cancel`); toast.success(`${o.order_number} cancelled`); await loadOrders(); loadInv(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const adjustStock = async (item, delta) => {
    const q = Math.max(0, item.quantity + delta);
    setInventory((inv) => inv.map((i) => (i.id === item.id ? { ...i, quantity: q } : i)));
    try { await api.put(`/inventory/${item.id}`, { quantity: q }); } catch (e) { loadInv(); }
  };
  const setStock = async (item, value, el) => {
    const q = Math.max(0, Number(value) || 0);
    if (q < item.quantity) {
      toast.error("To reduce stock, use Adjust so the reason is recorded.");
      if (el) el.value = item.quantity;
      return;
    }
    if (q === item.quantity) return;
    try { await api.put(`/inventory/${item.id}`, { quantity: q }); toast.success(`${item.name} updated`); loadInv(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); loadInv(); }
  };

  const collect = async () => {
    try {
      await api.post(`/invoices/${payFor.id}/pay`, { amount: Number(payAmt), mode: payMode, remarks: payRemarks });
      toast.success("Payment recorded"); setPayFor(null); setPayAmt(""); setPayRemarks(""); loadInvoices();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const activeOrders = orders.filter((o) => COLS.some((c) => c.key === o.status));
  const filteredInv = invoices.filter((i) => !search ||
    i.invoice_number.toLowerCase().includes(search.toLowerCase()) ||
    i.member_name.toLowerCase().includes(search.toLowerCase()));

  const nav = [
    { key: "orders", label: "Order Queue", icon: LayoutGrid },
    { key: "inventory", label: "Live Inventory", icon: Boxes },
    { key: "menu", label: "Menu", icon: UtensilsCrossed },
    { key: "billing", label: "Billing & Invoices", icon: ReceiptText },
    { key: "expenses", label: "Expenses", icon: Receipt },
    { key: "operations", label: "Club Operations", icon: CalendarClock },
  ];

  return (
    <Shell navItems={nav} active={tab} onNav={setTab} accent="staff">
      <div className="px-5 lg:px-8 py-6 lg:py-8">
        {tab === "orders" && (
          <>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-1">Kitchen Pass</p>
                <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text">Order Queue</h1>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={toggleClub} data-testid="club-status-pill" className={`text-xs font-semibold px-3 py-2 rounded-sm inline-flex items-center gap-2 transition-colors ${settings.club_open ? "bg-brand-ready text-white hover:bg-brand-ready/90" : "bg-brand-alert text-white hover:bg-brand-alert/90"}`}>
                  <Power className="w-4 h-4" /> {settings.club_open ? "Club Open — Tap to Close" : "Club Closed — Tap to Open"}
                </button>
                <Button onClick={loadOrders} data-testid="refresh-orders" variant="outline" className="rounded-sm gap-2 border-brand-line"><RefreshCw className="w-4 h-4" /></Button>
              </div>
            </div>
            {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div> : (
              <div className="flex gap-4 overflow-x-auto pb-4">
                {COLS.map((col) => {
                  const items = activeOrders.filter((o) => o.status === col.key);
                  return (
                    <div key={col.key} data-testid={`kanban-${col.key}`} className="min-w-[260px] w-[260px] shrink-0 bg-white/50 rounded-md border border-brand-line">
                      <div className="px-4 py-3 border-b border-brand-line flex items-center justify-between">
                        <h3 className="font-semibold text-brand-text text-xs uppercase tracking-wider">{col.title}</h3>
                        <span className="text-xs bg-brand-primary text-white rounded-full w-6 h-6 flex items-center justify-center">{items.length}</span>
                      </div>
                      <div className="p-3 space-y-3 min-h-[160px]">
                        {items.length === 0 && <p className="text-xs text-brand-muted px-1 py-4">Empty.</p>}
                        {items.map((o) => (
                          <div key={o.id} data-testid={`order-card-${o.id}`} className="bg-white border border-brand-line rounded-sm p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-mono text-[11px] text-brand-muted">{o.order_number}</span>
                              <span className="text-[11px] text-brand-muted">{fmtTime(o.created_at)}</span>
                            </div>
                            <p className="font-semibold text-brand-text text-sm">{o.member_name}</p>
                            {o.member_address && <p className="text-[11px] text-brand-muted leading-snug" data-testid={`order-address-${o.id}`}>{o.member_address}</p>}
                            <span className="text-[10px] uppercase tracking-wider text-brand-gold">{o.payment_mode}</span>
                            <div className="space-y-0.5 my-2">
                              {o.items.map((it, i) => <p key={i} className="text-xs text-brand-text"><span className="text-brand-gold font-semibold">{it.quantity}×</span> {it.name}</p>)}
                            </div>
                            <p className="text-xs font-semibold mb-2">{money(o.total, sym)}</p>
                            <div className="flex gap-1">
                              <Button onClick={() => act(o)} data-testid={`action-order-${o.id}`} className="flex-1 h-8 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm text-[11px] gap-1">
                                {NEXT[o.status].label} <ChevronRight className="w-3 h-3" />
                              </Button>
                              <button onClick={() => cancel(o)} data-testid={`cancel-order-${o.id}`} className="w-8 h-8 border border-brand-alert/40 text-brand-alert rounded-sm flex items-center justify-center hover:bg-brand-alert/10"><X className="w-4 h-4" /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === "inventory" && (
          <>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-1">Stock Control</p>
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text mb-6">Live Inventory</h1>
            <div className="bg-white border border-brand-line rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider">Item</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Category</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Stock</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {inventory.map((item) => {
                    const low = item.quantity <= item.low_stock_threshold;
                    return (
                      <TableRow key={item.id} data-testid={`inventory-row-${item.id}`} className={low ? "bg-brand-alert/5" : "hover:bg-brand-bg/50"}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-brand-muted">{item.category}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Input type="number" min={item.quantity} defaultValue={item.quantity} key={item.quantity} data-testid={`stock-input-${item.id}`}
                              onBlur={(e) => setStock(item, e.target.value, e.target)} className="w-16 h-7 text-center px-1 border-brand-line" />
                            <button onClick={() => adjustStock(item, 1)} data-testid={`stock-plus-${item.id}`} title="Add stock" className="w-7 h-7 border border-brand-line rounded-sm flex items-center justify-center hover:bg-brand-bg"><Plus className="w-3 h-3" /></button>
                            <span className="text-xs text-brand-muted ml-1">{item.unit}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {low ? <span className="inline-flex items-center gap-1 text-brand-alert text-xs font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Low</span>
                                 : <span className="text-brand-ready text-xs font-semibold">In Stock</span>}
                            <Button onClick={() => setAdjItem(item)} data-testid={`adjust-${item.id}`} variant="outline" className="h-7 rounded-sm border-brand-line text-xs">Adjust</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {tab === "menu" && (
          <>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-1">Service</p>
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text mb-2">Menu Availability</h1>
            <p className="text-sm text-brand-muted mb-6">Availability follows stock automatically. Use Hide to remove an item from members' view.</p>
            <div className="bg-white border border-brand-line rounded-md overflow-hidden overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  {["Item", "Category", "Price", "Status", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {menu.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-brand-muted py-8">No menu items.</TableCell></TableRow>}
                  {menu.map((m) => (
                    <TableRow key={m.id} data-testid={`staff-menu-row-${m.id}`} className={m.hidden ? "opacity-60 hover:bg-brand-bg/50" : "hover:bg-brand-bg/50"}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell className="text-brand-muted">{m.category}</TableCell>
                      <TableCell>{money(m.price, sym)}</TableCell>
                      <TableCell>
                        {m.hidden ? <span className="text-xs font-semibold text-brand-muted">Hidden</span>
                          : m.available ? <span className="text-brand-ready text-xs font-semibold">Available</span>
                          : <span className="text-brand-alert text-xs font-semibold">Out of Stock</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {m.hidden ? (
                          <Button onClick={() => toggleHide(m)} data-testid={`staff-unhide-${m.id}`} variant="outline" className="h-8 rounded-sm border-brand-ready/40 text-brand-ready text-xs gap-1"><Eye className="w-3.5 h-3.5" /> Unhide</Button>
                        ) : (
                          <Button onClick={() => toggleHide(m)} data-testid={`staff-hide-${m.id}`} variant="outline" className="h-8 rounded-sm border-brand-line text-xs gap-1"><EyeOff className="w-3.5 h-3.5" /> Hide</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {tab === "billing" && (
          <>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-1">Register</p>
                <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text">Billing & Invoices</h1>
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search invoice / member" data-testid="invoice-search" className="pl-9 w-64 border-brand-line" />
              </div>
            </div>
            <div className="bg-white border border-brand-line rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider">Invoice</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Member</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Total</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Due</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filteredInv.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} className="hover:bg-brand-bg/50">
                      <TableCell className="font-medium">{inv.invoice_number}<p className="text-[11px] text-brand-muted font-normal">{fmtDate(inv.created_at)}</p></TableCell>
                      <TableCell>{inv.member_name}</TableCell>
                      <TableCell>{money(inv.total, sym)}</TableCell>
                      <TableCell>{money(inv.balance_due, sym)}</TableCell>
                      <TableCell><StatusBadge status={inv.cancelled ? "cancelled" : inv.status} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {!inv.cancelled && inv.balance_due > 0 && inv.payment_mode !== "pay_now" && inv.payment_mode !== "credit" && (
                            <Button onClick={() => { setPayFor(inv); setPayAmt(String(inv.balance_due)); setPayMode("cash"); setPayRemarks(""); }} data-testid={`collect-${inv.id}`} className="h-8 bg-brand-gold hover:bg-brand-gold-hover text-white rounded-sm text-xs gap-1"><IndianRupee className="w-3 h-3" /> Collect</Button>
                          )}
                          <Button onClick={() => setViewInv(inv)} data-testid={`reprint-${inv.id}`} variant="outline" className="h-8 rounded-sm border-brand-line text-xs">Print</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        {tab === "expenses" && <ExpensesPanel role="staff" />}

        {tab === "operations" && <ClubOpsPanel role="staff" onViewPending={() => setTab("orders")} />}
      </div>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-sm bg-brand-bg" data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">Collect Payment</DialogTitle><DialogDescription className="sr-only">Record a payment against this invoice</DialogDescription></DialogHeader>
          {payFor && <div className="space-y-3">
            <p className="text-sm text-brand-muted">{payFor.invoice_number} · Balance {money(payFor.balance_due, sym)}</p>
            <div>
              <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">Payment Method</p>
              <div className="grid grid-cols-2 gap-2">
                {[{ k: "cash", l: "Cash" }, { k: "online", l: "Online" }].map((p) => (
                  <button key={p.k} onClick={() => setPayMode(p.k)} data-testid={`paymode-${p.k}`}
                    className={`text-sm px-3 py-2 rounded-sm border transition-colors ${payMode === p.k ? "bg-brand-primary text-white border-brand-primary" : "bg-white border-brand-line"}`}>{p.l}</button>
                ))}
              </div>
            </div>
            <Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} data-testid="payment-amount" className="border-brand-line" placeholder="Amount" />
            <div>
              <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">Remarks (optional)</p>
              <Input value={payRemarks} onChange={(e) => setPayRemarks(e.target.value)} data-testid="payment-remarks" className="border-brand-line" placeholder="Txn ref, receipt no, notes..." />
            </div>
          </div>}
          <DialogFooter>
            <Button onClick={collect} data-testid="confirm-payment" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InvoiceDialog invoice={viewInv} open={!!viewInv} onClose={() => setViewInv(null)} />
      <StockAdjustDialog item={adjItem} open={!!adjItem} onClose={() => setAdjItem(null)} onDone={loadInv} />
    </Shell>
  );
}
